import { useEffect, useMemo, useRef, useState } from 'react';
import { Search, X } from 'lucide-react';
import { POST_STATUSES } from '../../../shared/types';
import type { PlatformId, PostStatus } from '../../../shared/types';
import { PLATFORM_LIST } from '../../../shared/platforms';
import { postStatusLabel } from '../../../shared/utils';
import { Button, Input, Select } from '../ui';
import { debounce } from '../../lib/utils';

export interface PostFilters {
  status: PostStatus | '';
  platform: PlatformId | '';
  search: string;
}

export interface PostFilterBarProps {
  value: PostFilters;
  onChange: (next: PostFilters) => void;
  className?: string;
}

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  ...POST_STATUSES.map((status) => ({ value: status, label: postStatusLabel(status) })),
];

const PLATFORM_OPTIONS = [
  { value: '', label: 'All platforms' },
  ...PLATFORM_LIST.map((spec) => ({
    value: spec.id,
    label: spec.comingSoon ? `${spec.shortName} (soon)` : spec.shortName,
  })),
];

/** Status + platform + search row for the posts list. Owned by WEB-3. */
export function PostFilterBar({ value, onChange, className }: PostFilterBarProps) {
  const hasFilters = value.status !== '' || value.platform !== '' || value.search !== '';

  // The search box keeps its own live text so every keystroke doesn't refetch;
  // only the debounced value is pushed up to drive the query.
  const [searchText, setSearchText] = useState(value.search);
  const valueRef = useRef(value);
  valueRef.current = value;

  useEffect(() => setSearchText(value.search), [value.search]);

  const commitSearch = useMemo(
    () => debounce((next: string) => onChange({ ...valueRef.current, search: next }), 350),
    [onChange],
  );

  return (
    <div className={className}>
      <div className="flex flex-wrap items-center gap-2">
        <Select
          aria-label="Filter by status"
          selectSize="sm"
          wrapperClassName="w-auto min-w-36"
          options={STATUS_OPTIONS}
          value={value.status}
          onChange={(event) =>
            onChange({ ...value, status: event.target.value as PostStatus | '' })
          }
        />
        <Select
          aria-label="Filter by platform"
          selectSize="sm"
          wrapperClassName="w-auto min-w-36"
          options={PLATFORM_OPTIONS}
          value={value.platform}
          onChange={(event) =>
            onChange({ ...value, platform: event.target.value as PlatformId | '' })
          }
        />
        <Input
          aria-label="Search posts"
          inputSize="sm"
          leadingIcon={<Search className="size-3.5" />}
          placeholder="Search posts…"
          value={searchText}
          onChange={(event) => {
            setSearchText(event.target.value);
            commitSearch(event.target.value);
          }}
          wrapperClassName="w-full min-w-40 flex-1 sm:w-56 sm:flex-none"
        />
        {hasFilters && (
          <Button
            variant="ghost"
            size="sm"
            leftIcon={<X className="size-3.5" />}
            onClick={() => {
              setSearchText('');
              onChange({ status: '', platform: '', search: '' });
            }}
          >
            Clear
          </Button>
        )}
      </div>
    </div>
  );
}
